import React from 'react'
import styled, { css } from 'styled-components'
import { darken } from 'polished'
import PropTypes from 'prop-types'
import { classNames, variationName } from './../../utils/styles'
import { handleMouseUpByBlurring } from './../../utils/focus'
import tinycolor from 'tinycolor2'

import UnstyledLink from './../UnstyledLink'

import * as styles from './Button.scss'
import Icon from '../Icon'
import Spinner from '../Spinner'

const baseButtonStyle = css`
  ${({ theme, primary, outline }) => {
    if (primary) {
      return `
        background-color: ${theme.primaryColor};
        color: ${tinycolor(theme.primaryColor).getLuminance() > 0.5 ? '#101010' : '#f0f0f0'};
        &:hover,
        &:active {
          background-color: ${darken(0.1, theme.primaryColor)};
          color: ${tinycolor(theme.primaryColor).getLuminance() > 0.5 ? '#101010' : '#f0f0f0'};
          text-decoration: none;
        }
        > span {
          text-transform: uppercase !important;
        }
      `
    }
    if (outline) {
      return `
        background-color: transparent;
        color: ${theme.primaryColor};
        border: 1px solid ${theme.primaryColor};
        &:hover,
        &:active {
          background-color: ${theme.primaryColor};
          color: ${tinycolor(theme.primaryColor).getLuminance() > 0.5 ? '#101010' : '#f0f0f0'};
          text-decoration: none;
        }
      `
    }
  }};
`

// eslint-disable-next-line
const StyledButtonLink = styled(({ primary, ...rest }) => React.createElement(UnstyledLink, { ...rest }))`
  ${baseButtonStyle};
`
const StyledButtonButton = styled.button`
  ${baseButtonStyle};
`
const StyledButtonContent = styled.span`
  display: inline-block;
  ${({ isLoading }) => (isLoading ? `visibility: hidden;` : '')};
`

const StyledButtonSpinner = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`

export default function Button({
  id,
  url,
  disabled,
  loading,
  children,
  accessibilityLabel,
  ariaControls,
  ariaExpanded,
  onClick,
  onFocus,
  onBlur,
  external,
  icon,
  primary = false,
  outline,
  destructive,
  disclosure,
  plain,
  submit,
  size,
  fullWidth,
  className,
}) {
  const isDisabled = disabled || loading
  const classes = classNames(
    styles.Button,
    className && className,
    primary && styles.primary,
    outline && styles.outline,
    destructive && styles.destructive,
    isDisabled && styles.disabled,
    loading && styles.loading,
    plain && styles.plain,
    size && styles[variationName('size', size)],
    fullWidth && styles.fullWidth,
    icon && children == null && styles.iconOnly,
  )

  const disclosureIconMarkup = disclosure ? (
    <span className={styles.Icon}>
      <Icon source={loading ? 'placeholder' : 'caretDown'} />
    </span>
  ) : null

  const iconMarkup = icon ? (
    <span className={styles.Icon}>
      <Icon source={loading ? 'placeholder' : icon} />
    </span>
  ) : null

  const childMarkup = children ? <span>{children}</span> : null

  const content =
    iconMarkup || disclosureIconMarkup ? (
      <span className={styles.Content}>
        {iconMarkup}
        {childMarkup}
        {disclosureIconMarkup}
      </span>
    ) : (
      <span className={styles.Content}>{childMarkup}</span>
    )

  const type = submit ? 'submit' : 'button'

  return url ? (
    <StyledButtonLink
      as={UnstyledLink}
      primary={primary ? 1 : 0}
      outline={outline ? 1 : 0}
      id={id}
      url={url}
      external={external}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      onMouseUp={handleMouseUpByBlurring}
      className={classes}
      disabled={isDisabled}
      aria-label={accessibilityLabel}>
      <StyledButtonContent isLoading={loading}>{content}</StyledButtonContent>
      {loading && (
        <StyledButtonSpinner>
          <Spinner size="small" color="white" />
        </StyledButtonSpinner>
      )}
    </StyledButtonLink>
  ) : (
    <StyledButtonButton
      as="button"
      primary={primary ? 1 : 0}
      outline={outline ? 1 : 0}
      id={id}
      type={type}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      onMouseUp={handleMouseUpByBlurring}
      className={classes}
      disabled={isDisabled}
      aria-label={accessibilityLabel}
      aria-controls={ariaControls}
      aria-expanded={ariaExpanded}
      role={loading ? 'alert' : undefined}
      aria-busy={loading ? true : undefined}>
      <StyledButtonContent isLoading={loading}>{content}</StyledButtonContent>
      {loading && (
        <StyledButtonSpinner>
          <Spinner size="small" color="white" />
        </StyledButtonSpinner>
      )}
    </StyledButtonButton>
  )
}

Button.propTypes = {
  id: PropTypes.string,
  url: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  children: PropTypes.any,
  accessibilityLabel: PropTypes.string,
  ariaControls: PropTypes.string,
  ariaExpanded: PropTypes.string,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  external: PropTypes.bool,
  icon: PropTypes.element,
  primary: PropTypes.bool,
  outline: PropTypes.bool,
  destructive: PropTypes.bool,
  disclosure: PropTypes.bool,
  plain: PropTypes.bool,
  submit: PropTypes.bool,
  size: PropTypes.string,
  fullWidth: PropTypes.bool,
}



// WEBPACK FOOTER //
// ./src/components/Button/index.js